import React from "react";
import styled, { keyframes, css } from "styled-components";

// Inline-SVG cel-cartoon teacher — dep-free, crisp at any scale. ORIGINAL
// Dioscuro character: a boy-genius "lab kid" instructor drawn in a flat
// cel-cartoon style (thick black outlines, flat fills, oversized head, round
// glasses, lab coat, gloves). Inspired by the general Saturday-morning
// lab-cartoon look — NOT a copy of any trademarked character.
//
// viewBox 120×180. `action` drives the pose (same contract as before):
//   idle  → mouth closed, still.
//   talk  → mouth-flap loop (open/closed) via a stepped @keyframes.
//   point → right arm raised with a pointer toward the caption/code (screen-right).
//   walk  → whole sprite drifts a couple px left↔right.
//
// Palette: skin (#f2c9a0), hair (#4a2f1a), coat (#f4f4f4 / shade #d9d9d9),
// shirt (#2f6f5f), gloves (#3a6fd8), pants (#37475a), shoes (#1a1a1a),
// outline (#1a1a1a), lens (#eaf6ff), pointer (#7a4a1f / tip #d4a843).

const OUTLINE = "#1a1a1a";

// ── mouth talk loop: 2-frame open/closed cycle, stepped ──────────────────
const talkLoop = keyframes`
  0%, 49%   { opacity: 1; }
  50%, 100% { opacity: 0; }
`;

// ── walk: subtle horizontal drift ────────────────────────────────────────
const walkDrift = keyframes`
  0%, 100% { transform: translateX(-3px); }
  50%      { transform: translateX(3px); }
`;

const Svg = styled.svg`
  display: block;
  ${({ $walk }) =>
    $walk &&
    css`
      animation: ${walkDrift} 900ms steps(2, end) infinite;
    `}
`;

// Open-mouth shape only exists in "talk"; blink it on a stepped loop so it
// reads as a 2-frame mouth flap.
const TalkMouth = styled.ellipse`
  animation: ${talkLoop} 300ms steps(1, end) infinite;
`;

const InstructorSprite = ({ action = "idle", scale = 4 }) => {
  // Base grid is 20×30 "cells"; the viewBox is 6× finer (120×180) for smooth
  // curves, while width/height stay proportional to `scale` like before.
  const baseW = 20;
  const baseH = 30;
  const isTalk = action === "talk";
  const isPoint = action === "point";
  const isWalk = action === "walk";

  return (
    <Svg
      viewBox="0 0 120 180"
      width={baseW * scale}
      height={baseH * scale}
      $walk={isWalk}
      role="img"
      aria-label={`instructor ${action}`}
    >
      {/* ===================== LEGS + SHOES ===================== */}
      <rect x="46" y="138" width="12" height="26" rx="3" fill="#37475a" stroke={OUTLINE} strokeWidth="3" />
      <rect x="62" y="138" width="12" height="26" rx="3" fill="#37475a" stroke={OUTLINE} strokeWidth="3" />
      <ellipse cx="50" cy="166" rx="11" ry="6" fill="#1a1a1a" stroke={OUTLINE} strokeWidth="3" />
      <ellipse cx="70" cy="166" rx="11" ry="6" fill="#1a1a1a" stroke={OUTLINE} strokeWidth="3" />

      {/* ===================== LAB COAT (torso) ===================== */}
      {/* coat body: shoulders wide, flaring to the hem */}
      <path
        d="M34 96 Q60 84 86 96 L92 150 Q60 158 28 150 Z"
        fill="#f4f4f4"
        stroke={OUTLINE}
        strokeWidth="3"
        strokeLinejoin="round"
      />
      {/* coat opening (center seam) */}
      <path d="M60 90 L60 152" stroke={OUTLINE} strokeWidth="2.5" />
      {/* coat side shade */}
      <path d="M78 98 L84 148 Q72 152 66 152 L66 96 Z" fill="#e4e4e4" />
      {/* shirt V-collar (teal) under the coat */}
      <path d="M52 92 L60 108 L68 92 Z" fill="#2f6f5f" stroke={OUTLINE} strokeWidth="2.5" strokeLinejoin="round" />
      {/* coat lapels */}
      <path d="M52 92 L60 108 L54 118 L46 100 Z" fill="#ffffff" stroke={OUTLINE} strokeWidth="2.5" strokeLinejoin="round" />
      <path d="M68 92 L60 108 L66 118 L74 100 Z" fill="#ffffff" stroke={OUTLINE} strokeWidth="2.5" strokeLinejoin="round" />
      {/* pocket */}
      <rect x="70" y="126" width="14" height="12" rx="2" fill="#ffffff" stroke={OUTLINE} strokeWidth="2.5" />

      {/* ===================== LEFT ARM (screen-left) ===================== */}
      <path d="M34 98 Q26 118 30 138 L40 138 Q38 116 44 100 Z" fill="#f4f4f4" stroke={OUTLINE} strokeWidth="3" strokeLinejoin="round" />
      {/* left glove */}
      <circle cx="33" cy="142" r="8" fill="#3a6fd8" stroke={OUTLINE} strokeWidth="3" />

      {/* ===================== RIGHT ARM — rest or point ===================== */}
      {isPoint ? (
        <>
          {/* raised sleeve angling up-right */}
          <path d="M86 96 Q100 86 110 74 L104 66 Q92 80 78 92 Z" fill="#f4f4f4" stroke={OUTLINE} strokeWidth="3" strokeLinejoin="round" />
          {/* right glove at the raised hand */}
          <circle cx="108" cy="70" r="8" fill="#3a6fd8" stroke={OUTLINE} strokeWidth="3" />
          {/* pointer stick angled up-right toward the caption side (kept inside the viewBox) */}
          <rect x="101" y="44" width="5" height="32" rx="2.5" transform="rotate(38 103 60)" fill="#7a4a1f" stroke={OUTLINE} strokeWidth="2" />
          <circle cx="113" cy="49" r="4" fill="#d4a843" stroke={OUTLINE} strokeWidth="2" />
        </>
      ) : (
        <>
          <path d="M86 98 Q94 118 90 138 L80 138 Q82 116 76 100 Z" fill="#f4f4f4" stroke={OUTLINE} strokeWidth="3" strokeLinejoin="round" />
          {/* right glove */}
          <circle cx="87" cy="142" r="8" fill="#3a6fd8" stroke={OUTLINE} strokeWidth="3" />
        </>
      )}

      {/* ===================== NECK ===================== */}
      <rect x="54" y="82" width="12" height="10" fill="#f2c9a0" stroke={OUTLINE} strokeWidth="3" />

      {/* ===================== HEAD ===================== */}
      {/* big round head */}
      <circle cx="60" cy="50" r="34" fill="#f2c9a0" stroke={OUTLINE} strokeWidth="3" />
      {/* hair — cropped top with a little side-part tuft */}
      <path
        d="M28 44 Q30 16 60 16 Q90 16 92 44 Q84 30 60 30 Q40 30 34 42 Q31 40 28 44 Z"
        fill="#4a2f1a"
        stroke={OUTLINE}
        strokeWidth="3"
        strokeLinejoin="round"
      />
      {/* one cowlick tuft */}
      <path d="M58 16 Q66 6 72 14 Q66 16 58 20 Z" fill="#4a2f1a" stroke={OUTLINE} strokeWidth="3" strokeLinejoin="round" />
      {/* ears */}
      <circle cx="27" cy="52" r="6" fill="#f2c9a0" stroke={OUTLINE} strokeWidth="3" />
      <circle cx="93" cy="52" r="6" fill="#f2c9a0" stroke={OUTLINE} strokeWidth="3" />

      {/* GLASSES — two big round lenses + bridge (the teacher tell) */}
      <circle cx="47" cy="52" r="14" fill="#eaf6ff" stroke={OUTLINE} strokeWidth="3.5" />
      <circle cx="73" cy="52" r="14" fill="#eaf6ff" stroke={OUTLINE} strokeWidth="3.5" />
      <path d="M61 52 H59" stroke={OUTLINE} strokeWidth="3.5" />
      {/* lens glare */}
      <path d="M40 46 Q44 44 48 46" stroke="#ffffff" strokeWidth="2.5" fill="none" strokeLinecap="round" />
      <path d="M66 46 Q70 44 74 46" stroke="#ffffff" strokeWidth="2.5" fill="none" strokeLinecap="round" />
      {/* eyes behind the lenses */}
      <circle cx="47" cy="53" r="4" fill={OUTLINE} />
      <circle cx="73" cy="53" r="4" fill={OUTLINE} />

      {/* eyebrows */}
      <path d="M40 36 Q47 33 54 36" stroke={OUTLINE} strokeWidth="3" fill="none" strokeLinecap="round" />
      <path d="M66 36 Q73 33 80 36" stroke={OUTLINE} strokeWidth="3" fill="none" strokeLinecap="round" />

      {/* MOUTH — idle/point/walk = smile line; talk = flapping open oval */}
      {isTalk ? (
        <>
          {/* closed frame (visible inverse half of the flap cycle) */}
          <path d="M52 70 Q60 76 68 70" stroke="#7a3a1f" strokeWidth="3" fill="none" strokeLinecap="round" />
          {/* open frame — dark oval, blinks in on the talk loop */}
          <TalkMouth cx="60" cy="72" rx="7" ry="5" fill="#7a2b1a" stroke={OUTLINE} strokeWidth="2.5" />
        </>
      ) : (
        <path d="M52 70 Q60 76 68 70" stroke="#7a3a1f" strokeWidth="3" fill="none" strokeLinecap="round" />
      )}
    </Svg>
  );
};

export default InstructorSprite;
